/**
 * Qedara Chat — UI sounds synthesized with the Web Audio API.
 *
 * No audio files and no libraries: every tone is scheduled on oscillators, so
 * nothing extra ships in the bundle and nothing is fetched at runtime.
 *
 * Browsers keep an AudioContext suspended until the user has interacted with
 * the page. Every entry point therefore goes through `unlockAudio()`, and the
 * module also arms a one-shot listener so the first click or keypress anywhere
 * in the app resumes the context.
 */

const RING_MAX_MS = 30000;  // hard stop: nobody answered

let ctx = null;
let ringTimer = null;       // interval driving the repeat
let ringStopTimer = null;   // the 30s cut-off
let ringNodes = [];         // oscillators of the current cycle, so stopRing() can silence them
let ringSeq = 0;            // guards against a stopRing() that lands mid-unlock

function audioContext() {
  if (ctx) return ctx;
  const Ctor = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

export function unlockAudio() {
  const ac = audioContext();
  if (!ac) return Promise.resolve(false);
  if (ac.state !== "suspended") return Promise.resolve(true);
  return ac.resume().then(() => true, () => false);
}

if (typeof window !== "undefined") {
  const arm = () => { unlockAudio(); };
  ["pointerdown", "keydown", "touchstart"].forEach((evt) =>
    window.addEventListener(evt, arm, { once: true, passive: true })
  );
}

/**
 * One enveloped sine tone. The short attack and exponential release matter:
 * gating a raw oscillator on and off clicks audibly.
 */
function tone(ac, { freq, start, duration, gain = 0.08 }) {
  const osc = ac.createOscillator();
  const amp = ac.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, start);
  amp.gain.setValueAtTime(0.0001, start);
  amp.gain.exponentialRampToValueAtTime(gain, start + 0.012);
  amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(amp).connect(ac.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
  return osc;
}

/** Two rising notes, ~220ms total — a new message arrived. */
export function notification() {
  return unlockAudio().then((ok) => {
    const ac = audioContext();
    if (!ok || !ac) return false;
    const t = ac.currentTime;
    tone(ac, { freq: 880.0, start: t, duration: 0.1 });           // A5
    tone(ac, { freq: 1318.5, start: t + 0.1, duration: 0.12 });   // E6
    return true;
  });
}

/**
 * Repeating call tone until `stopRing()`, or 30s, whichever comes first.
 * `subtle` is the outgoing variant played to whoever placed the call: one quiet
 * low tone, so it reads as progress rather than as someone ringing you.
 */
export function ring({ subtle = false } = {}) {
  stopRing();
  const seq = ++ringSeq;
  return unlockAudio().then((ok) => {
    const ac = audioContext();
    if (!ok || !ac || seq !== ringSeq) return false;  // stopped, or superseded, while unlocking
    const cycle = () => {
      const t = ac.currentTime;
      // The previous cycle's tones have already ended, so the list can reset.
      ringNodes = subtle
        ? [tone(ac, { freq: 440.0, start: t, duration: 0.6, gain: 0.025 })]
        : [
            tone(ac, { freq: 587.33, start: t, duration: 0.45, gain: 0.09 }),        // D5
            tone(ac, { freq: 783.99, start: t + 0.5, duration: 0.45, gain: 0.09 }),  // G5
          ];
    };
    cycle();
    ringTimer = setInterval(cycle, subtle ? 2000 : 1500);  // ~1s on, 0.5s off
    ringStopTimer = setTimeout(stopRing, RING_MAX_MS);
    return true;
  });
}

export function stopRing() {
  ringSeq += 1;
  if (ringTimer) { clearInterval(ringTimer); ringTimer = null; }
  if (ringStopTimer) { clearTimeout(ringStopTimer); ringStopTimer = null; }
  ringNodes.forEach((osc) => { try { osc.stop(); } catch { /* already finished */ } });
  ringNodes = [];
  return Promise.resolve(true);
}
